import React, { Suspense, lazy } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { AppRoutes } from './router';

// 🚀 延遲載入組件
const PerformanceOptimizer = lazy(() => import('./components/feature/PerformanceOptimizer'));
const GoogleTagManager = lazy(() => import('./components/feature/GoogleTagManager'));
const FacebookPixel = lazy(() => import('./components/feature/FacebookPixel'));
const GoogleAnalytics = lazy(() => import('./components/feature/GoogleAnalytics'));

// 🚀 載入中組件
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="loading-spinner mx-auto mb-4"></div>
      <div className="text-gray-600">載入中...</div>
    </div>
  </div>
);

// 🚀 錯誤邊界組件
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('應用程式錯誤:', error, errorInfo);
    
    // 發送錯誤到分析工具
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.message,
        fatal: false
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="max-w-md mx-auto text-center p-6">
            <div className="text-6xl mb-4">😔</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              糟糕！發生了一些問題
            </h1>
            <p className="text-gray-600 mb-6">
              我們的技術團隊已經收到通知，請稍後再試或聯繫客服。
            </p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary w-full"
              >
                重新整理頁面
              </button>
              <a
                href="tel:0978900081"
                className="btn btn-secondary w-full"
              >
                <i className="ri-phone-line mr-2"></i>
                聯繫客服：0978-900-081
              </a>
            </div>
            
            {import.meta.env.MODE === 'development' && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500">
                  技術詳情 (開發模式)
                </summary>
                <pre className="mt-2 text-xs text-red-600 overflow-auto">
                  {this.state.error?.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 🚀 主應用組件
const App: React.FC = () => {
  return (
    <ErrorBoundary>
      <BrowserRouter basename={__BASE_PATH__}>
        {/* 🚀 效能優化組件 */}
        <Suspense fallback={null}>
          <PerformanceOptimizer />
        </Suspense>

        {/* 🚀 追蹤組件 - 延遲載入 */}
        <Suspense fallback={null}>
          <GoogleTagManager gtmId="GTM-NPGL5P2F" />
          <GoogleAnalytics />
          <FacebookPixel />
        </Suspense>

        {/* 🚀 主要路由 */}
        <Suspense fallback={<LoadingFallback />}>
          <div className="app-container">
            {/* 🎯 跳過連結 (無障礙優化) */}
            <a 
              href="#main-content" 
              className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-white px-4 py-2 rounded-md shadow-lg z-50 text-sm font-medium text-gray-900 focus-ring"
            >
              跳到主要內容
            </a>

            {/* 🚀 主要內容區域 */}
            <main id="main-content" className="min-h-screen">
              <AppRoutes />
            </main>

            {/* 🚀 效能提示 (僅開發模式) */}
            {import.meta.env.MODE === 'development' && (
              <div className="fixed bottom-4 right-4 bg-black/80 text-white text-xs p-2 rounded z-50 max-w-xs">
                <div>開發模式</div>
                <div>效能監控已啟用</div>
              </div>
            )}
          </div>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
